import IntelligentContentParser from './IntelligentContentParser';
import {
  mergeSmartPasteEntryList,
  mergeSmartPasteEquipment,
  stampSmartPasteEntryList,
  stampSmartPasteEquipment
} from './SmartPasteEntryMerge';
import { hydrateCharacterCombatState } from './CharacterCombatReadiness';
import { isAmmunitionEntry, isExplicitlyEquippedWeapon } from './EquipmentState';

const WEAPON_NAME_HINTS = /(axe|bow|club|crossbow|dagger|dart|flail|glaive|halberd|hammer|handaxe|javelin|lance|longsword|longbow|mace|maul|morningstar|pike|quarterstaff|rapier|scimitar|shortbow|shortsword|sickle|sling|spear|staff|sword|trident|war ?pick|warhammer|whip)/i;

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeEquipment = (incoming, existing) => {
  const copyCurrency = (value) => (
    value && typeof value === 'object' && !Array.isArray(value)
      ? { ...value }
      : {}
  );
  const getTypeLabel = (entry) => String(
    entry?.type
    || entry?.kind
    || entry?.category
    || entry?.itemType
    || entry?.entryType
    || ''
  ).trim().toLowerCase();
  const looksLikeWeaponEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return false;
    if (isAmmunitionEntry(entry)) return false;

    const typeLabel = getTypeLabel(entry);
    if (/\bweapon\b/i.test(typeLabel)) return true;
    if (/(armor|shield|consumable|potion|tool|kit|focus|wondrous|gear|valuable|currency|coin|gem|jewel)/i.test(typeLabel)) {
      return false;
    }

    const combinedText = [
      entry?.name,
      entry?.description,
      entry?.notes,
      entry?.statLine
    ].filter((part) => typeof part === 'string' && part.trim()).join('\n');

    return Boolean(
      WEAPON_NAME_HINTS.test(String(entry?.name || ''))
      || /\b[+-]?\d+\s+to\s+hit\b/i.test(combinedText)
      || /\b\d+d\d+(?:\s*[+-]\s*\d+)?\b/i.test(combinedText)
      || /\b(range|reach)\s+\d+/i.test(combinedText)
      || /\b(cleave|graze|nick|push|sap|slow|topple|vex)\b/i.test(combinedText)
      || typeof entry?.attackBonus === 'number'
      || typeof entry?.toHit === 'number'
      || typeof entry?.damageFormula === 'string'
      || typeof entry?.damage === 'string'
      || entry?.attack
    );
  };
  const createBuckets = () => ({
    weapons: [],
    armor: [],
    items: [],
    valuables: [],
    currency: {}
  });
  const bucketEntry = (buckets, entry, sourceCollection = 'equipment') => {
    if (!entry) return;
    if (typeof entry !== 'object') {
      buckets.items.push(entry);
      return;
    }

    const normalizedWeapon = isExplicitlyEquippedWeapon(entry, sourceCollection) && looksLikeWeaponEntry(entry)
      ? IntelligentContentParser.normalizeEquipmentWeaponEntry({
          ...entry,
          type: 'weapon'
        })
      : null;
    if (normalizedWeapon) {
      buckets.weapons.push(normalizedWeapon);
      return;
    }

    const typeLabel = getTypeLabel(entry);
    if (typeLabel === 'armor' || typeLabel === 'shield') {
      buckets.armor.push(entry);
      return;
    }
    if (typeLabel === 'valuable') {
      buckets.valuables.push(entry);
      return;
    }

    buckets.items.push(entry);
  };

  if (!incoming) {
    if (!existing) {
      return { weapons: [], armor: [], items: [], valuables: [], currency: {} };
    }
    return {
      weapons: [...(existing.weapons || [])],
      armor: [...(existing.armor || [])],
      items: [...(existing.items || [])],
      valuables: [...(existing.valuables || [])],
      currency: copyCurrency(existing.currency)
    };
  }
  if (Array.isArray(incoming)) {
    const buckets = createBuckets();
    incoming.forEach((entry) => bucketEntry(buckets, entry, 'equipment'));
    return buckets;
  }

  const buckets = createBuckets();
  (incoming.weapons || []).forEach((entry) => bucketEntry(buckets, entry, 'equipment.weapons'));
  (incoming.armor || []).forEach((entry) => bucketEntry(buckets, entry, 'equipment.armor'));
  (incoming.items || []).forEach((entry) => bucketEntry(buckets, entry, 'equipment.items'));
  (incoming.valuables || []).forEach((entry) => bucketEntry(buckets, entry, 'equipment.valuables'));
  (incoming.equipment || []).forEach((entry) => bucketEntry(buckets, entry, 'equipment'));
  (incoming.other || []).forEach((entry) => bucketEntry(buckets, entry, 'equipment.other'));
  buckets.currency = {
    ...copyCurrency(existing?.currency),
    ...copyCurrency(incoming.currency)
  };

  return {
    weapons: buckets.weapons.length ? buckets.weapons : [...(existing?.weapons || [])],
    armor: buckets.armor.length ? buckets.armor : [...(existing?.armor || [])],
    items: buckets.items.length ? buckets.items : [...(existing?.items || [])],
    valuables: buckets.valuables.length ? buckets.valuables : [...(existing?.valuables || [])],
    currency: buckets.currency
  };
};

const normalizeSpells = (incoming, existing) => {
  const baseline = existing || {
    spells: [],
    prepared: [],
    known: [],
    atWill: [],
    byLevel: {},
    slots: {},
    innateByFrequency: {},
    alwaysPrepared: {}
  };
  if (!incoming) {
    return {
      spells: [...(baseline.spells || [])],
      prepared: [...(baseline.prepared || [])],
      known: [...(baseline.known || [])],
      atWill: [...(baseline.atWill || [])],
      byLevel: { ...(baseline.byLevel || {}) },
      slots: { ...(baseline.slots || {}) },
      innateByFrequency: { ...(baseline.innateByFrequency || {}) },
      alwaysPrepared: { ...(baseline.alwaysPrepared || {}) }
    };
  }
  if (Array.isArray(incoming)) {
    return {
      spells: [...incoming],
      prepared: [...(baseline.prepared || [])],
      known: [...(baseline.known || [])],
      atWill: [...(baseline.atWill || [])],
      byLevel: { ...(baseline.byLevel || {}) },
      slots: { ...(baseline.slots || {}) },
      innateByFrequency: { ...(baseline.innateByFrequency || {}) },
      alwaysPrepared: { ...(baseline.alwaysPrepared || {}) }
    };
  }
  return {
    spells: [...(incoming.spells || baseline.spells || [])],
    prepared: [...(incoming.prepared || baseline.prepared || [])],
    known: [...(incoming.known || baseline.known || [])],
    atWill: [...(incoming.atWill || baseline.atWill || [])],
    byLevel: { ...(incoming.byLevel || baseline.byLevel || {}) },
    slots: { ...(incoming.slots || baseline.slots || {}) },
    innateByFrequency: { ...(incoming.innateByFrequency || baseline.innateByFrequency || {}) },
    alwaysPrepared: { ...(incoming.alwaysPrepared || baseline.alwaysPrepared || {}) }
  };
};

const normalizeAbilities = (incoming, existing) => {
  if (!incoming) return existing || {};
  const result = { ...(existing || {}) };
  Object.entries(incoming).forEach(([key, value]) => {
    const normalized = toNumber(value);
    result[key] = normalized !== null ? normalized : value;
  });
  return result;
};

const normalizeDeathSaves = (incoming, existing) => {
  const base = existing || { successes: 0, failures: 0 };
  if (!incoming) return base;
  return {
    successes: incoming.successes ?? base.successes ?? 0,
    failures: incoming.failures ?? base.failures ?? 0
  };
};

// GAP-007 bug #7: preserve full SpeedModes object so fly/swim/climb/burrow speeds are not
// dropped when applying a SmartPaste suggestion.
// rule_reference: D&D 5e 2024 — creatures may have walk, fly, swim, climb, and burrow speeds.
// date_of_change: 2026-05-20
const normalizeSpeed = (incoming, existing) => {
  if (incoming === null || incoming === undefined) return existing ?? null;
  if (typeof incoming === 'number') {
    return Number.isFinite(incoming) ? { walk: incoming } : (existing ?? null);
  }
  if (typeof incoming === 'string') {
    const parsed = toNumber(incoming);
    return parsed !== null ? { walk: parsed } : (existing ?? null);
  }
  if (typeof incoming === 'object' && !Array.isArray(incoming)) {
    const modes = {};
    for (const [mode, val] of Object.entries(incoming)) {
      const n = toNumber(val);
      if (n !== null) modes[mode.toLowerCase()] = n;
    }
    if (Object.keys(modes).length > 0) return modes;
  }
  return existing ?? null;
};

const normalizeAc = (incoming, existing) => {
  if (incoming === null || incoming === undefined) return existing ?? null;
  if (typeof incoming === 'number') return incoming;
  if (typeof incoming === 'object') {
    const value = toNumber(incoming.value);
    if (value !== null) return value;
  }
  const numeric = toNumber(incoming);
  return numeric !== null ? numeric : existing ?? null;
};

const normalizeHpValues = (incoming, existing) => {
  const seed = existing || {};
  let current = seed.hp ?? seed.current ?? null;
  let max = seed.maxHp ?? seed.maximum ?? null;
  const assign = (value) => {
    const parsed = toNumber(value);
    if (parsed !== null) {
      if (max === null) max = parsed;
      if (current === null) current = parsed;
    }
  };
  if (incoming !== null && incoming !== undefined) {
    if (typeof incoming === 'number' || typeof incoming === 'string') {
      const parsed = toNumber(incoming);
      if (parsed !== null) {
        current = parsed;
        if (max === null) max = parsed;
      }
    } else if (typeof incoming === 'object') {
      assign(incoming.current);
      assign(incoming.maximum);
      assign(incoming.average);
      assign(incoming.value);
    }
  }
  return { current, max };
};

const normalizeList = (incoming, existing) => {
  if (!incoming) return Array.isArray(existing) ? [...existing] : [];
  if (Array.isArray(incoming)) return [...incoming];
  return Array.isArray(existing) ? [...existing] : [];
};

const stripGenericActionSummaryEntries = (entries, field) => {
  if (!Array.isArray(entries)) return [];

  return entries.filter((entry) => {
    const name = String(entry?.name || '').trim();
    if (!name) return false;
    if (IntelligentContentParser.isCharacterSheetMetadataDump(entry)) return false;

    if (field === 'actions' && /^(attack action|spellcasting|weapon\/attack\/cantrip)$/i.test(name)) {
      return false;
    }

    return true;
  });
};

const normalizeProficiencies = (incoming, existing) => {
  const base = (existing && typeof existing === 'object') ? existing : {};
  const next = (incoming && typeof incoming === 'object') ? incoming : {};
  return {
    ...base,
    ...next,
    armor: [...(next.armor || base.armor || [])],
    weapons: [...(next.weapons || base.weapons || [])],
    tools: [...(next.tools || base.tools || [])],
    savingThrows: [...(next.savingThrows || base.savingThrows || [])],
    languages: [...(next.languages || base.languages || [])]
  };
};

export const buildSmartPasteCharacterImport = (baseCharacter, data) => {
  if (!data) return baseCharacter;

  const enrichedData = IntelligentContentParser.deriveCharacterImportData(data);
  const replacesCharacterIdentity = Boolean(
    enrichedData.name
    || enrichedData.class
    || (Array.isArray(enrichedData.classes) && enrichedData.classes.length > 0)
    || enrichedData.race
    || enrichedData.species
    || enrichedData.level
  );
  const hpEnvelope = normalizeHpValues(enrichedData.hp, baseCharacter);

  if (enrichedData.maxHp !== undefined && enrichedData.maxHp !== null) {
    const override = toNumber(enrichedData.maxHp);
    if (override !== null) {
      hpEnvelope.max = override;
      if (hpEnvelope.current === null) {
        hpEnvelope.current = override;
      }
    }
  }

  if (enrichedData.hp !== null && enrichedData.hp !== undefined) {
    const direct = toNumber(enrichedData.hp);
    if (direct !== null) {
      hpEnvelope.current = direct;
      if (hpEnvelope.max === null) {
        hpEnvelope.max = direct;
      }
    }
  }

  const importedProficiencies = normalizeProficiencies(
    enrichedData.proficiencies,
    replacesCharacterIdentity ? {} : baseCharacter.proficiencies
  );
  const importedToolProficiencies = normalizeList(
    Array.isArray(enrichedData.toolProficiencies) ? enrichedData.toolProficiencies : importedProficiencies.tools,
    replacesCharacterIdentity ? [] : baseCharacter.toolProficiencies
  );
  const importedLanguages = normalizeList(
    Array.isArray(enrichedData.languages) ? enrichedData.languages : importedProficiencies.languages,
    replacesCharacterIdentity ? [] : baseCharacter.languages
  );
  const baseActions = replacesCharacterIdentity ? [] : (Array.isArray(baseCharacter.actions) ? baseCharacter.actions : []);
  const baseBonusActions = replacesCharacterIdentity ? [] : (Array.isArray(baseCharacter.bonusActions) ? baseCharacter.bonusActions : []);
  const baseReactions = replacesCharacterIdentity ? [] : (Array.isArray(baseCharacter.reactions) ? baseCharacter.reactions : []);

  const mergedCharacter = {
    ...baseCharacter,
    sourceFormat: enrichedData.sourceFormat ?? baseCharacter.sourceFormat,
    sourceCharacterId: enrichedData.sourceCharacterId ?? baseCharacter.sourceCharacterId,
    completeCharacterImport: enrichedData.completeCharacterImport ?? baseCharacter.completeCharacterImport ?? false,
    name: enrichedData.name ?? baseCharacter.name,
    class: enrichedData.class ?? enrichedData.classes?.[0]?.name ?? baseCharacter.class,
    classes: normalizeList(enrichedData.classes, baseCharacter.classes),
    subclass: enrichedData.subclass ?? enrichedData.classes?.[0]?.subclass ?? baseCharacter.subclass,
    level: enrichedData.level ?? enrichedData.classes?.[0]?.level ?? baseCharacter.level ?? 1,
    experience: enrichedData.experience ?? baseCharacter.experience ?? baseCharacter.xp ?? 0,
    race: enrichedData.race ?? enrichedData.species ?? baseCharacter.race,
    species: enrichedData.species ?? enrichedData.race ?? baseCharacter.species,
    background: enrichedData.background ?? (replacesCharacterIdentity ? '' : baseCharacter.background),
    alignment: enrichedData.alignment ?? baseCharacter.alignment ?? '',
    inspiration: enrichedData.inspiration ?? baseCharacter.inspiration ?? false,
    proficiencyBonus: enrichedData.proficiencyBonus ?? baseCharacter.proficiencyBonus,
    initiative: enrichedData.initiative ?? baseCharacter.initiative,
    speed: normalizeSpeed(enrichedData.speed, baseCharacter.speed),
    passivePerception: enrichedData.passivePerception ?? baseCharacter.passivePerception,
    size: enrichedData.size ?? baseCharacter.size ?? 'Medium',
    ac: normalizeAc(enrichedData.ac, baseCharacter.ac),
    hp: hpEnvelope.current ?? baseCharacter.hp ?? baseCharacter.maxHp ?? null,
    maxHp: hpEnvelope.max ?? baseCharacter.maxHp ?? hpEnvelope.current ?? null,
    tempHp: enrichedData.tempHp ?? baseCharacter.tempHp ?? null,
    hitDice: enrichedData.hitDice ?? enrichedData.hitDiceTotal ?? baseCharacter.hitDice ?? '',
    hitDiceSpent: enrichedData.hitDiceSpent ?? baseCharacter.hitDiceSpent ?? 0,
    abilities: normalizeAbilities(enrichedData.abilities, baseCharacter.abilities),
    deathSaves: normalizeDeathSaves(enrichedData.deathSaves, baseCharacter.deathSaves),
    equipment: stampSmartPasteEquipment(
      normalizeEquipment(
        enrichedData.equipment ?? (Array.isArray(enrichedData.inventory?.equipment) ? enrichedData.inventory.equipment : null),
        replacesCharacterIdentity ? null : baseCharacter.equipment
      )
    ),
    inventory: enrichedData.inventory ?? baseCharacter.inventory ?? null,
    features: stampSmartPasteEntryList(normalizeList(enrichedData.features, baseCharacter.features), 'features'),
    classFeatures: stampSmartPasteEntryList(normalizeList(enrichedData.classFeatures, baseCharacter.classFeatures), 'classFeatures'),
    feats: stampSmartPasteEntryList(normalizeList(enrichedData.feats, baseCharacter.feats), 'feats'),
    speciesTraits: stampSmartPasteEntryList(normalizeList(enrichedData.speciesTraits, baseCharacter.speciesTraits), 'speciesTraits'),
    classFeaturesText: enrichedData.classFeaturesText ?? baseCharacter.classFeaturesText ?? '',
    speciesTraitsText: enrichedData.speciesTraitsText ?? baseCharacter.speciesTraitsText ?? (baseCharacter.racialTraits || ''),
    featsText: enrichedData.featsText ?? baseCharacter.featsText ?? '',
    allies: enrichedData.biography?.allies ?? enrichedData.allies ?? baseCharacter.allies ?? '',
    // @date_of_change: 2026-03-19
    // rule_reference: Full-character SmartPaste imports must preserve explicit authored action economy entries.
    // Derived class features still augment these later during hydration, but authored actions/reactions remain canonical source data.
    actions: stampSmartPasteEntryList(
      stripGenericActionSummaryEntries(normalizeList(enrichedData.actions, baseActions), 'actions'),
      'actions'
    ),
    bonusActions: stampSmartPasteEntryList(
      stripGenericActionSummaryEntries(normalizeList(enrichedData.bonusActions, baseBonusActions), 'bonusActions'),
      'bonusActions'
    ),
    reactions: stampSmartPasteEntryList(
      stripGenericActionSummaryEntries(normalizeList(enrichedData.reactions, baseReactions), 'reactions'),
      'reactions'
    ),
    spells: normalizeSpells(enrichedData.spells, baseCharacter.spells),
    spellcasting: enrichedData.spellcasting ? { ...(baseCharacter.spellcasting || {}), ...enrichedData.spellcasting } : baseCharacter.spellcasting,
    proficiencies: importedProficiencies,
    toolProficiencies: importedToolProficiencies,
    languages: importedLanguages,
    notes: enrichedData.notes ?? baseCharacter.notes,
    senses: enrichedData.senses ?? baseCharacter.senses,
    savingThrows: enrichedData.savingThrows ?? baseCharacter.savingThrows,
    skills: enrichedData.skills ?? baseCharacter.skills,
    skillsDetailed: enrichedData.skillsDetailed ?? baseCharacter.skillsDetailed ?? [],
    artwork: enrichedData.artwork ?? baseCharacter.artwork,
    tokenAssetKey: enrichedData.tokenAssetKey ?? baseCharacter.tokenAssetKey,
    personalityTraits: enrichedData.biography?.personality ?? enrichedData.personalityTraits ?? baseCharacter.personalityTraits ?? '',
    ideals: enrichedData.biography?.ideals ?? enrichedData.ideals ?? baseCharacter.ideals ?? '',
    bonds: enrichedData.biography?.bonds ?? enrichedData.bonds ?? baseCharacter.bonds ?? '',
    flaws: enrichedData.biography?.flaws ?? enrichedData.flaws ?? baseCharacter.flaws ?? '',
    motivations: enrichedData.biography?.goals ?? enrichedData.motivations ?? enrichedData.goals ?? baseCharacter.motivations ?? '',
    appearance: enrichedData.appearance ?? enrichedData.biography?.appearance ?? enrichedData.description ?? baseCharacter.appearance ?? '',
    fears: enrichedData.biography?.fears ?? enrichedData.fears ?? baseCharacter.fears ?? '',
    quirks: enrichedData.biography?.quirks ?? enrichedData.quirks ?? baseCharacter.quirks ?? '',
    tactics: enrichedData.biography?.tactics ?? enrichedData.tactics ?? baseCharacter.tactics ?? '',
    voiceMannerisms: enrichedData.biography?.voiceMannerisms ?? enrichedData.voiceMannerisms ?? baseCharacter.voiceMannerisms ?? '',
    hooks: enrichedData.biography?.hooks ?? enrichedData.hooks ?? baseCharacter.hooks ?? '',
    factions: enrichedData.factions ?? baseCharacter.factions ?? [],
    role: enrichedData.role ?? baseCharacter.role ?? '',
    affiliation: enrichedData.affiliation ?? baseCharacter.affiliation ?? '',
    biography: enrichedData.biography ?? baseCharacter.biography,
    title: enrichedData.title ?? baseCharacter.title ?? '',
    aliases: enrichedData.aliases ?? baseCharacter.aliases ?? [],
    challengeRating: enrichedData.challengeRating ?? baseCharacter.challengeRating ?? '',
    xp: enrichedData.xp ?? baseCharacter.xp ?? '',
    legendaryActions: enrichedData.legendaryActions ?? baseCharacter.legendaryActions ?? null,
    mythicActions: enrichedData.mythicActions ?? baseCharacter.mythicActions ?? null,
    lairActions: enrichedData.lairActions ?? baseCharacter.lairActions ?? null,
    regionalEffects: enrichedData.regionalEffects ?? baseCharacter.regionalEffects ?? null,
    minions: enrichedData.minions ?? baseCharacter.minions ?? null,
    multiclassFeatures: enrichedData.multiclassFeatures ?? baseCharacter.multiclassFeatures ?? null,
    resistances: enrichedData.resistances ?? baseCharacter.resistances ?? [],
    immunities: enrichedData.immunities ?? baseCharacter.immunities ?? [],
    vulnerabilities: enrichedData.vulnerabilities ?? baseCharacter.vulnerabilities ?? [],
    conditionImmunities: enrichedData.conditionImmunities ?? baseCharacter.conditionImmunities ?? [],
    conditions: enrichedData.conditions ?? baseCharacter.conditions ?? []
  };

  return hydrateCharacterCombatState(mergedCharacter);
};

export const projectSmartPasteCharacterRecords = (baseCharacter, records) => {
  const recordList = Array.isArray(records) ? records.filter(Boolean) : (records ? [records] : []);
  let projectedCharacter = hydrateCharacterCombatState(
    baseCharacter && typeof baseCharacter === 'object'
      ? { ...baseCharacter }
      : {}
  );

  const setProjectedCharacter = (updates) => {
    projectedCharacter = hydrateCharacterCombatState({
      ...projectedCharacter,
      ...updates
    });
  };

  recordList.forEach((record) => {
    const field = record?.field;
    const action = record?.action;
    const data = record?.data;

    switch (field) {
      case 'all': {
        if (action === 'replace' && data && typeof data === 'object') {
          projectedCharacter = buildSmartPasteCharacterImport(projectedCharacter, data);
        }
        break;
      }
      case 'name':
      case 'class':
      case 'subclass':
      case 'background':
      case 'alignment':
      case 'notes':
      case 'title':
      case 'role':
      case 'affiliation':
      case 'appearance':
      case 'backstory':
      case 'personality':
      case 'personalityTraits':
      case 'ideals':
      case 'bonds':
      case 'flaws':
      case 'motivations':
      case 'hooks': {
        setProjectedCharacter({
          [field === 'personality' ? 'personalityTraits' : field]: typeof data === 'string' ? data : String(data || '')
        });
        break;
      }
      case 'race':
      case 'species': {
        const value = typeof data === 'string' ? data : String(data || '');
        setProjectedCharacter({
          race: value,
          species: value
        });
        break;
      }
      case 'level':
      case 'experience':
      case 'initiative':
      case 'proficiencyBonus':
      case 'passivePerception':
      case 'maxHp': {
        const numeric = toNumber(data);
        if (numeric !== null) {
          setProjectedCharacter({ [field]: numeric });
        }
        break;
      }
      case 'hp': {
        const numeric = toNumber(data);
        const hpEnvelope = numeric !== null
          ? { current: numeric, max: numeric }
          : normalizeHpValues(data, projectedCharacter);
        setProjectedCharacter({
          hp: hpEnvelope.current ?? projectedCharacter.hp ?? projectedCharacter.maxHp ?? null,
          maxHp: hpEnvelope.max ?? projectedCharacter.maxHp ?? hpEnvelope.current ?? null
        });
        break;
      }
      case 'ac': {
        setProjectedCharacter({ ac: normalizeAc(data, projectedCharacter.ac) });
        break;
      }
      case 'speed': {
        setProjectedCharacter({ speed: normalizeSpeed(data, projectedCharacter.speed) });
        break;
      }
      case 'abilities': {
        setProjectedCharacter({
          abilities: normalizeAbilities(data, projectedCharacter.abilities)
        });
        break;
      }
      case 'classes':
      case 'languages':
      case 'toolProficiencies':
      case 'skillsDetailed':
      case 'factions':
      case 'resistances':
      case 'immunities':
      case 'vulnerabilities':
      case 'conditionImmunities': {
        setProjectedCharacter({
          [field]: normalizeList(data, projectedCharacter[field])
        });
        break;
      }
      case 'savingThrows':
      case 'skills':
      case 'senses': {
        if (data && typeof data === 'object') {
          setProjectedCharacter({
            [field]: {
              ...(projectedCharacter[field] && typeof projectedCharacter[field] === 'object' ? projectedCharacter[field] : {}),
              ...data
            }
          });
        }
        break;
      }
      case 'proficiencies': {
        setProjectedCharacter({
          proficiencies: normalizeProficiencies(data, projectedCharacter.proficiencies)
        });
        break;
      }
      case 'spellcasting': {
        setProjectedCharacter({
          spellcasting: data && typeof data === 'object'
            ? { ...(projectedCharacter.spellcasting || {}), ...data }
            : (projectedCharacter.spellcasting || {})
        });
        break;
      }
      case 'spells': {
        const nextSpells = action === 'append'
          ? (() => {
              const baseline = normalizeSpells(null, projectedCharacter.spells);
              if (Array.isArray(data)) {
                return {
                  ...baseline,
                  known: [...(baseline.known || []), ...data]
                };
              }
              if (data && typeof data === 'object') {
                return normalizeSpells({
                  ...baseline,
                  ...data
                }, projectedCharacter.spells);
              }
              return baseline;
            })()
          : normalizeSpells(data, projectedCharacter.spells);
        setProjectedCharacter({ spells: nextSpells });
        break;
      }
      case 'equipment': {
        const nextEquipment = action === 'replace'
          ? stampSmartPasteEquipment(normalizeEquipment(data, projectedCharacter.equipment))
          : mergeSmartPasteEquipment(projectedCharacter.equipment, data);
        setProjectedCharacter({ equipment: nextEquipment });
        break;
      }
      case 'currency': {
        const equipment = projectedCharacter.equipment && typeof projectedCharacter.equipment === 'object' && !Array.isArray(projectedCharacter.equipment)
          ? projectedCharacter.equipment
          : {};
        setProjectedCharacter({
          equipment: {
            ...equipment,
            currency: {
              ...(action === 'replace' ? {} : (equipment.currency || {})),
              ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {})
            }
          }
        });
        break;
      }
      case 'features':
      case 'classFeatures':
      case 'feats':
      case 'speciesTraits':
      case 'actions':
      case 'bonusActions':
      case 'reactions': {
        const additions = stripGenericActionSummaryEntries(
          Array.isArray(data) ? data : [data],
          field
        );
        if (!additions.length) break;
        const nextEntries = action === 'replace'
          ? stampSmartPasteEntryList(additions, field)
          : mergeSmartPasteEntryList(projectedCharacter[field], additions, field);
        setProjectedCharacter({ [field]: nextEntries });
        break;
      }
      case 'classFeaturesText':
      case 'speciesTraitsText':
      case 'featsText': {
        setProjectedCharacter({
          [field]: typeof data === 'string' ? data : String(data || '')
        });
        break;
      }
      default:
        break;
    }
  });

  return hydrateCharacterCombatState(projectedCharacter);
};

export default buildSmartPasteCharacterImport;
